
import React from 'react';

interface ProgressBarProps {
  /** Current active step (1‑based). Values outside the range are clamped. */
  currentStep: number;
}

/**
 * A horizontal progress bar that displays the current step of a multi‑step
 * workflow. Invalid `currentStep` values are safely handled by clamping them
 * between the first and last step.
 */
export default function ProgressBar({ currentStep }: ProgressBarProps) {
  // Define the steps once – the order is important for rendering.
  const steps = [
    { number: 1, label: 'Lieu' },
    { number: 2, label: 'Catégorie' },
    { number: 3, label: 'Prestation' },
    { number: 4, label: 'Adresse' },
    { number: 5, label: 'Date' },
    { number: 6, label: 'Confirmation' },
  ];

  // -------------------------------------------------------------------------
  // 1️⃣  Defensive handling of out‑of‑range values
  // -------------------------------------------------------------------------
  // Clamp the value so the component never crashes if a parent passes
  // an invalid step (e.g. 0, -1 or 10). This also protects the conditional
  // class calculations below.
  const safeCurrentStep = Math.min(
    Math.max(currentStep, steps[0].number),
    steps[steps.length - 1].number,
  );

  return (
    <div className="mb-8">
      {/* ---------- Step circles & connecting lines ---------- */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                safeCurrentStep >= step.number
                  ? 'bg-primary text-white'
                  : 'bg-dark-card border-2 border-dark-border text-gray-500'
              }`}
            >
              {step.number}
            </div>

            {/* Render the connector line except after the last step */}
            {index < steps.length - 1 && (
              <div
                className={`w-12 h-1 mx-2 transition-all duration-300 ${
                  safeCurrentStep > step.number ? 'bg-primary' : 'bg-dark-border'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* ---------- Step labels ---------- */}
      <div className="flex items-center justify-between mt-2">
        {steps.map((step) => (
          <div
            key={step.number}
            className={`text-xs ${
              safeCurrentStep >= step.number ? 'text-primary' : 'text-gray-500'
            }`}
            style={{ width: '80px', textAlign: 'center' }}
          >
            {step.label}
          </div>
        ))}
      </div>
    </div>
  );
}
